import * as XLSX from 'xlsx'

/**
 * Converts an Excel file (xlsx/xls) to CSV format
 * @param file - The Excel file to convert
 * @returns Promise<File> - A new File object containing the CSV content
 * @throws Error if the file cannot be read or converted
 */
export const convertExcelToCSV = async (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer)
        const workbook = XLSX.read(data, { type: 'array' })

        // Get the first worksheet
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]

        // Convert to CSV
        const csvContent = XLSX.utils.sheet_to_csv(worksheet)

        // Create a new File object with CSV content
        const csvFile = new File(
          [csvContent],
          file.name.replace(/\.(xlsx|xls)$/i, '.csv'),
          { type: 'text/csv' }
        )

        resolve(csvFile)
      } catch (error) {
        reject(error)
      }
    }

    reader.onerror = () => {
      reject(new Error('Failed to read the Excel file'))
    }

    reader.readAsArrayBuffer(file)
  })
}

/**
 * Checks if a file is an Excel file based on its extension
 * @param file - The file to check
 * @returns boolean - True if the file is an Excel file
 */
export const isExcelFile = (file: File): boolean => {
  return /\.(xlsx|xls)$/i.test(file.name)
}

/**
 * Converts a file to CSV if it's an Excel file, otherwise returns the original file
 * @param file - The file to process
 * @returns Promise<File> - The CSV file or original file
 */
export const convertToCSVIfNeeded = async (file: File): Promise<File> => {
  if (isExcelFile(file)) {
    return await convertExcelToCSV(file)
  }
  return file
}
